import { useMemo, useState } from "react";
import * as R from "ramda";
import cx from "classnames";

import "./table.scss";

import {
  any,
  arrayOf,
  bool,
  func,
  node,
  number,
  oneOf,
  shape,
  string,
} from "prop-types";

const propTypes = {
  className: string,
  colgroup: node, // <colgroup> if needed
  columns: arrayOf(
    shape({
      id: any.isRequired, // something comparable, string, number
      header: node,
      render: func.isRequired,
      useTh: bool, // use a <th> instead of <td>
      initialSort: oneOf(["asc", "desc"]), // only one column should have this!
      sortValueFn: func, // passes an item from data, should always return a comparable value! (not null/undefined)
    }),
  ).isRequired,
  data: arrayOf(
    shape({
      // id is used as key for each row, needed for sorting data
      // see https://medium.com/@robinpokorny/index-as-a-key-is-an-anti-pattern-e0349aece318
      id: any.isRequired,
    }),
  ).isRequired,
  perPage: number,
  // renderRow provides the ability to render the row with classes/click handlers,
  // or maybe add another expanding row, etc
  // passed the tds and the row data (see default below)
  renderRow: func,
  tfoot: node,
};

const defaultProps = {
  renderRow: (columns, rowData) => <tr key={rowData.id}>{columns}</tr>,
};

const Table = (props) => {
  const { className, colgroup, columns, data, perPage, renderRow, tfoot } =
    props;

  const [sort, updateSort] = useSort(columns);
  const [page, setPage] = useState(1);

  const sortFn = useMemo(() => {
    return R.compose(
      R.prop("sortValueFn"),
      R.find(R.propEq("id", sort.columnId)),
    )(columns);
  }, [columns, sort.columnId]);

  const processedData = useMemo(() => {
    return R.compose(
      R.when(() => !R.isNil(perPage), R.take(perPage * page)),
      R.when(
        () => sort.columnId && R.type(sortFn) === "Function",
        (data) => {
          const directionFn = sort.isAscending ? R.ascend : R.descend;

          // javascript footgun ho!
          // sort will not be correct if there are undefineds or nulls
          // so only sort the non-nil values and stick the nils at the end
          return R.compose(
            (pair) => [...(pair.notNils || []), ...(pair.nils || [])],
            R.over(
              R.lensProp("notNils"),
              R.compose(R.sort(directionFn(sortFn)), R.defaultTo([])),
            ),
            R.groupBy((item) => (R.isNil(sortFn(item)) ? "nils" : "notNils")),
          )(data);
        },
      ),
      R.defaultTo([]),
    )(data);
  }, [data, page, perPage, sort.columnId, sort.isAscending, sortFn]);

  const someRowsHidden = processedData.length !== data.length;

  return (
    <div className={cx("table", "container", className)}>
      <table>
        {colgroup}
        <thead>
          <tr>
            {columns.map((column, i) => {
              return (
                <th key={i}>
                  {columnIsSortable(column) ? (
                    <button
                      type="button"
                      onClick={() => updateSort(column)}
                      className={cx("table__column", {
                        "table__column--sortable": columnIsSortable(column),
                        "table__column--is-sorted-asc":
                          sort.columnId === column.id && sort.isAscending,
                        "table__column--is-sorted-desc":
                          sort.columnId === column.id && !sort.isAscending,
                      })}
                    >
                      <span>{column.header}</span>
                    </button>
                  ) : (
                    <div className="table__column">{column.header}</div>
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {processedData.map((rowData, i) => {
            const tds = columns.map((column, j) => {
              return column.useTh ? (
                <th key={j}>{column.render(rowData)}</th>
              ) : (
                <td key={j}>{column.render(rowData)}</td>
              );
            });

            return renderRow(tds, rowData);
          })}
        </tbody>
        {tfoot}
      </table>

      {perPage && someRowsHidden && (
        <button
          type="button"
          className="btn btn--caps table__show-more"
          onClick={() => setPage((p) => p + 1)}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="feather feather-chevron-down"
          >
            <polyline points="6 9 12 15 18 9"></polyline>
          </svg>
          Show more
        </button>
      )}
    </div>
  );
};

Table.propTypes = propTypes;
Table.defaultProps = defaultProps;
export default Table;

const columnIsSortable = (column) =>
  R.type(R.prop("sortValueFn", column)) === "Function";

const columnHasInitialSort = (column) =>
  R.compose(
    R.either(R.equals("asc"), R.equals("desc")),
    R.prop("initialSort"),
  )(column);

/**
 * state {columnId, isAscending}
 */
function useSort(columns) {
  const initialSort = R.compose(
    R.applySpec({
      columnId: R.propOr(null, "id"),
      isAscending: R.propEq("initialSort", "asc"),
    }),
    R.defaultTo({}),
    R.find(R.both(columnHasInitialSort, columnIsSortable)),
  )(columns);

  const [sort, setSort] = useState(initialSort);

  const updateSort = (column) => {
    if (columnIsSortable(column)) {
      setSort((oldSort) => ({
        columnId: column.id,
        isAscending:
          oldSort.columnId === column.id ? !oldSort.isAscending : true,
      }));
    }
  };

  return [sort, updateSort];
}
